import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XClose } from '@timelyapp/icons/solid-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import React from 'react';
import { Box } from '../../box/Box';
import { Text } from '../../text/Text';
import sharedStyles from '../radix-floating-elements.module.scss';

const DialogRoot = (props: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root modal {...props} />
);

const DialogTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>
>((props, forwardedRef) => <DialogPrimitive.Trigger {...props} ref={forwardedRef} />);

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    variant?: 'backdrop' | 'transparent';
  }
>((props, forwardedRef) => {
  const { variant = 'invisible', ...rest } = props;
  return (
    <DialogPrimitive.Overlay
      {...rest}
      ref={forwardedRef}
      className={classNames(
        sharedStyles.overlay,
        { [sharedStyles.visible]: variant === 'backdrop' },
        props.className
      )}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

const cvaContent = cva(sharedStyles.dialogContentBase, {
  variants: {
    /** Various modal layouts */
    variant: {
      /** Dialog is default on `Dialog` */
      dialog: sharedStyles['contentVariantDialog'],
      /** Default modal */
      modal: [sharedStyles.modalContentBase, sharedStyles.contentVariantModal],
      /** Minimal, unstyled modal */
      minimal: [sharedStyles.modalContentBase, sharedStyles.contentVariantMinimal],
      /** Overlay 100%/100% of screen */
      fullscreen: [sharedStyles.modalContentBase, sharedStyles.contentVariantModalFullscreen],
      /** ~99% overlay the content area of Timely, leaving nav visible */
      overlayView: [sharedStyles.modalContentBase, sharedStyles.contentVariantModalOverlayView],
      /** For "settings in modal" */
      settings: [sharedStyles.modalContentBase, sharedStyles.contentVariantModalSettings],
      /** For wizard modals */
      wizard: [sharedStyles.modalContentBase, sharedStyles.contentVariantModalWizard],
    },
  },
  defaultVariants: {
    variant: 'dialog',
  },
});

export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
  VariantProps<typeof cvaContent> & {
    /** Can this dialog be closed directly by user interaction? Hides X/etc if not set */
    closable?: boolean;
  };

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ children, variant, closable = true, ...props }, forwardedRef) => (
    <DialogPrimitive.Content
      asChild
      {...props}
      ref={forwardedRef}
      className={cvaContent({
        variant,
        className: props.className,
      })}
    >
      <Box.Column gap="medium">
        {children}
        {!!closable && (
          <Dialog.Close asChild>
            <button
              className={classNames(sharedStyles.closeButton, props.className)}
              aria-label="Close"
            >
              <XClose width={16} height={16} />
            </button>
          </Dialog.Close>
        )}
      </Box.Column>
    </DialogPrimitive.Content>
  )
);

const DialogClose = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>((props, forwardedRef) => (
  <DialogPrimitive.Close
    {...props}
    ref={forwardedRef}
    className={classNames(sharedStyles.closeButton, props.className)}
  />
));
DialogClose.displayName = 'DialogClose';

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ children, ...rest }, forwardedRef) => (
  <DialogPrimitive.Title
    {...rest}
    ref={forwardedRef}
    asChild
    className={classNames(sharedStyles.dialogTitle, rest.className)}
  >
    <Text.Headline size="small">{children}</Text.Headline>
  </DialogPrimitive.Title>
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ children, ...rest }, forwardedRef) => (
  <DialogPrimitive.Description
    {...rest}
    ref={forwardedRef}
    asChild
    className={classNames(sharedStyles.dialogDescription, rest.className)}
  >
    <Text>{children}</Text>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = 'DialogDescription';

/**
 * Home-grown below
 */
type DialogFooterProps = React.ComponentPropsWithoutRef<typeof Box.Row>;

const DialogFooter = (props: DialogFooterProps) => (
  <Box.Row {...props} className={classNames(sharedStyles.dialogFooter, props.className)} />
);
DialogFooter.displayName = 'DialogFooter';

export type DialogCompositionProps = Omit<React.HTMLAttributes<HTMLElement>, 'title'> &
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
    trigger: React.ReactElement;
    title?: React.ReactNode | React.ReactElement;
    children?: React.ReactNode;
    description?: React.ReactNode;
    buttons?: (React.ReactNode | 'spacer')[];
    variant?: DialogContentProps['variant'];
    closable?: DialogContentProps['closable'];
    backdrop?: boolean;

    /** Looks like we need to manually handle ESC events when modal is controlled? */
    onEscapeKeyDown?: DialogContentProps['onEscapeKeyDown'];
  };

export const DialogComposition = ({
  trigger,
  title,
  buttons,
  description,
  children,
  onEscapeKeyDown,
  onOpenChange,
  variant = 'dialog',
  backdrop = false,
  closable = true,
  style,
  ...rest
}: DialogCompositionProps) => {
  /**
   * Handle escape key down event.
   * This is a workaround for Radix Dialog not closing on ESC key press when controlled.
   * See: https://github.com/radix-ui/primitives/issues/2653 + https://github.com/radix-ui/primitives/issues/2563
   * @warning This workaround only works if the dialog is controlled.
   */
  const handleEscapeKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      onOpenChange?.(false);
      onEscapeKeyDown?.(event);
      // Also, good place to inject "are you sure you want to close?" logic?
    },
    [onEscapeKeyDown, onOpenChange]
  );

  const wrapAndStyleChildren = !!variant && ['settings', 'minimal', 'wizard'].includes(variant);

  return (
    <DialogRoot modal {...rest} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogPortal>
        <DialogOverlay variant={backdrop ? 'backdrop' : 'transparent'}>
          <DialogContent
            onEscapeKeyDown={handleEscapeKeyDown}
            closable={closable}
            style={style}
            variant={variant}
          >
            {!!title && <DialogTitle>{title}</DialogTitle>}
            {!!description && <DialogDescription>{description}</DialogDescription>}
            {wrapAndStyleChildren ? children : <DialogDescription>{children}</DialogDescription>}
            {!!buttons && (
              <DialogFooter>
                {buttons.map((button, index) => {
                  if (button === 'spacer') {
                    return <Box.Spacer key={index} />;
                  }
                  return button;
                })}
              </DialogFooter>
            )}
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </DialogRoot>
  );
};
DialogComposition.displayName = 'DialogComposition';

const Dialog = Object.assign(DialogComposition, {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Portal: DialogPortal,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Close: DialogClose,
  Title: DialogTitle,
  Description: DialogDescription,
  // Custom
  Footer: DialogFooter,
});
Dialog.displayName = 'Dialog';
Dialog.Trigger.displayName = 'Dialog.Trigger';
Dialog.Portal.displayName = 'Dialog.Portal';
Dialog.Overlay.displayName = 'Dialog.Overlay';
Dialog.Content.displayName = 'Dialog.Content';
Dialog.Close.displayName = 'Dialog.Close';
Dialog.Title.displayName = 'Dialog.Title';
Dialog.Description.displayName = 'Dialog.Description';
Dialog.Footer.displayName = 'Dialog.Footer';

export default Dialog;
